/**
 * useStackUnfurl
 *
 * State machine + FLIP orchestration that powers the inline unfurl of
 * a layer-2 Collection (a "Stack") inside a `<Stream>`. Owns:
 *
 *   1. The single `openStackId` selection across the entire Stream
 *      (one stack at a time — clicking another while one is open
 *      queues a close-then-open).
 *   2. The children query (`getCollectionDetails(stackId, { limit })`).
 *      This deliberately bypasses `collectionStore.useBlocks` — the
 *      unfurl is read-mostly today; drag-into / drag-out is a tracked
 *      follow-up.
 *   3. The two FLIP transitions, designed to mirror each other 1:1:
 *        opening — every existing grid child snaps to its BEFORE rect
 *                   and animates to AFTER; new unfurl items animate
 *                   from the stack tile's BEFORE rect, scaling up
 *                   from `CLOSE_SCALE` to 1, with a forward stagger
 *                   (front-most card first).
 *        closing — unfurl items leave grid flow (`position: absolute`
 *                   at their BEFORE rects), every other sibling FLIPs
 *                   back to AFTER, the unfurl items translate toward
 *                   the (post-reflow) tile rect while scaling down to
 *                   `CLOSE_SCALE`, with REVERSED stagger so the
 *                   front-most card is the last to land.
 *
 * The four FLIP rules every contributor needs to internalize:
 *
 *   1. Snapshot BEFORE rects synchronously inside `toggleStack` /
 *      the ESC handler — never inside a `useLayoutEffect`. By the
 *      time an effect runs, React has committed and the layout is
 *      already at AFTER.
 *   2. Force reflow between "snap" and "play". `void
 *      document.body.offsetHeight` between writing the inverse
 *      transform and enabling the transition. Without it browsers
 *      collapse two consecutive style writes into one computation
 *      and skip the animation.
 *   3. Re-check `phaseRef.current` inside every rAF / setTimeout
 *      callback. Close-mid-open is the common case; stale callbacks
 *      have to bail.
 *   4. Cleanup must be defensive. Each layout effect's return value
 *      restores `style.transition`, `style.transform`, and (for
 *      absolutely-positioned items) the full position-out-of-flow
 *      style group, in case the effect tears down mid-flight.
 *
 * The Stream splices the unfurl row directly after the stack tile's
 * index in `group.items` — no DOM measurement is needed to pick the
 * insert slot. The `data-stack-id` and `data-unfurl-item="true"`
 * markers are still set on the DOM elements so this hook's FLIP can
 * walk the relevant nodes, and so `StackFrame` can query them for
 * its geometry trace.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCollectionDetails } from "@/api/query-api";
import { blockStore } from "@/data/block-store";
import { useAnnotationsForBlocks } from "@/data/use-annotations";
import { ConfiguredLogger } from "@/lib/logger";
import { usePinList } from "@/state/pin-store";
import { useApp } from "@/state/app-context";
import "./stack-unfurl.scss";
/** Open + close FLIP duration. Kept in sync with the 260ms transitions
 *  in `stack-block-view.scss` (scatter, pill, close-circle) so the
 *  visual reset of the tile lands together with the row reflow. */
export const UNFURL_ANIM_MS = 260;
/** Scale factor applied to unfurl items at their nearest-tile end of
 *  both FLIPs (start of open, end of close). Approximates the size of
 *  a peek tile (~200px) inside the card slot so items appear to grow
 *  out of / shrink back to peek-tile size as they pass through the
 *  tile's footprint. */
const CLOSE_SCALE = 200 / 300; // ≈ 0.67
/** Spring ease-out — overshoots slightly then settles, which reads as
 *  more energetic and physical than a plain material ease. The overshoot
 *  is small (1.15) so cards don't visibly bounce past their targets. */
const UNFURL_EASING = "cubic-bezier(0.34, 1.15, 0.64, 1)";
/** Explicit identity targets keep Safari from treating the open FLIP's
 *  "animate back to an empty inline transform" as a no-op. The cleanup
 *  still clears the inline transform once the transition finishes. */
const IDENTITY_TRANSLATE = "translate3d(0px, 0px, 0)";
const IDENTITY_TRANSLATE_SCALE = "translate3d(0px, 0px, 0) scale(1)";
/** Per-item stagger delay added to the OPEN transition of unfurled
 *  items. Keep it tight — the goal is a quick cascade ripple, not a
 *  parade. At 25ms with a cap of 5 items the full cascade spread is
 *  100ms, well within the `opening → open` timer. */
const UNFURL_STAGGER_MS = 25;
/** Cap the stagger at this many items so a 100-item stack doesn't
 *  produce a 4-second cascade. Items beyond the cap all share the
 *  same delay as `MAX_STAGGER_ITEMS - 1`. */
const MAX_STAGGER_ITEMS = 5;
/** Extra slack added to the phase-transition timer to outlast the CSS
 *  transition's `transitionend` event in the worst case. */
const UNFURL_SAFETY_MS = 50;
/** Eager-load cap on the children query. Stacks > 100 children
 *  silently truncate today; pagination is a tracked follow-up.
 *
 *  Public so parent surfaces (e.g. `CollectionDetailTabContent`) can
 *  prefetch the same page size into the same query key — that's what
 *  makes the unfurl open feel instant: by click time the cache is
 *  already warm and the hook's `loading -> opening` transition
 *  resolves in the same render tick. */
export const STACK_DETAIL_PAGE_SIZE = 100;
/** Defensive cap on `walkAllGridChildren()` — a stream with 10k cards
 *  shouldn't realistically need to FLIP all of them; the visible
 *  viewport is ~30 cards and the inverse-transform cost dominates the
 *  open animation past ~500. Bounded inputs per the security-limits
 *  rule. */
const MAX_FLIPPED_CARDS = 500;
/** Stable fallback slot for the pin subscription when no stack is
 *  open. Hooks must run unconditionally, but the snapshot from this
 *  slot is discarded in that branch. Held as a module constant so
 *  referential identity is stable across renders. */
const NOOP_PIN_SCOPE = { type: "account" };
const logger = new ConfiguredLogger({ caller: "useStackUnfurl" });
// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------
function lookupStackTile(stackId) {
    const el = document.querySelector(`[data-stack-id="${CSS.escape(stackId)}"]`);
    return el ?? undefined;
}
/**
 * Every `.stream-group__cards > *` across the document, capped at
 * `MAX_FLIPPED_CARDS`. The Stream may have many groups (Pinned + one
 * per date), each a separate flex/grid container, and a stack
 * unfurled in any one of them can push siblings in every group below.
 */
function walkAllGridChildren() {
    const cards = [];
    const grids = document.querySelectorAll(".stream-group__cards");
    for (const grid of Array.from(grids)) {
        for (const child of Array.from(grid.children)) {
            if (cards.length >= MAX_FLIPPED_CARDS)
                return cards;
            if (child instanceof HTMLElement)
                cards.push(child);
        }
    }
    return cards;
}
function snapshotGridRects() {
    const map = new Map();
    for (const el of walkAllGridChildren()) {
        map.set(el, el.getBoundingClientRect());
    }
    return map;
}
function saveItemStyle(el) {
    return {
        position: el.style.position,
        top: el.style.top,
        left: el.style.left,
        width: el.style.width,
        height: el.style.height,
        margin: el.style.margin,
        transition: el.style.transition,
        transform: el.style.transform,
        opacity: el.style.opacity,
    };
}
function restoreItemStyle(el, saved) {
    el.style.position = saved.position;
    el.style.top = saved.top;
    el.style.left = saved.left;
    el.style.width = saved.width;
    el.style.height = saved.height;
    el.style.margin = saved.margin;
    el.style.transition = saved.transition;
    el.style.transform = saved.transform;
    el.style.opacity = saved.opacity;
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useStackUnfurl(options) {
    const envelopeOverrides = options?.envelopeOverrides;
    // `app` is read here so the Esc handler below can check
    // `app.state.ui.isMultiselectMode` synchronously at keypress time
    // and bail when multi-select is active — keeps the close-stack
    // shortcut from stealing Esc from the multi-select exit shortcut.
    // Read imperatively (not via `useStore`) so toggling multi-select
    // doesn't re-render every consumer of this hook.
    const app = useApp();
    const openStackId = app.useStore((s) => s.stackUnfurl.openStackId);
    const phase = app.useStore((s) => s.stackUnfurl.phase);
    const storedEnvelopes = app.useStore((s) => s.stackUnfurl.envelopes);
    const showUnfurledItems = app.useStore((s) => s.stackUnfurl.showUnfurledItems);
    const phaseRef = useRef("closed");
    const beforeRectsRef = useRef(new Map());
    const stackTileBeforeRectRef = useRef(undefined);
    const unfurlItemRefsRef = useRef(new Map());
    const pendingOpenStackRef = useRef(undefined);
    const pendingRafRef = useRef(undefined);
    const pendingTimerRef = useRef(undefined);
    const hasRunOpenFlipRef = useRef(false);
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);
    const setOpenStackId = useCallback((id) => {
        app.setStackUnfurl({ openStackId: id });
    }, [app]);
    const setPhase = useCallback((nextPhase) => {
        app.setStackUnfurl({ phase: nextPhase });
    }, [app]);
    // Publish the open-stack id to the app store so the omnibar can
    // retarget its destination chip while a stack is unfurled. Clear
    // the moment we start closing (rather than when the close animation
    // finishes) so the chip reverts to the route-level collection in
    // the same beat the user clicks to dismiss the stack — waiting for
    // the FLIP to complete would leave the chip stale for ~260ms.
    useEffect(() => {
        const publishedId = phase === "closing" || phase === "closed" ? undefined : openStackId;
        app.setOpenStackId(publishedId);
    }, [app, openStackId, phase]);
    // Clear the published stack id on unmount so a route change that
    // tears down the Stream doesn't leave a stale id pinned in the app
    // store, which would keep the omnibar chip stuck on a stack that no
    // longer has any visible surface to act on.
    useEffect(() => {
        return () => {
            app.setOpenStackId(undefined);
            app.resetStackUnfurl();
        };
    }, [app]);
    const cancelPending = useCallback(() => {
        if (pendingRafRef.current !== undefined) {
            cancelAnimationFrame(pendingRafRef.current);
            pendingRafRef.current = undefined;
        }
        if (pendingTimerRef.current !== undefined) {
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = undefined;
        }
    }, []);
    // -- Children query -----------------------------------------------------
    //
    // Synthetic stacks (homepage Stream's same-collection-per-day
    // groupings) carry their own pre-baked envelope list via
    // `envelopeOverrides` — the query is disabled for those ids since
    // they have no server identity and `getCollectionDetails` would 404.
    // Real stacks fall through to the normal fetch path.
    const hasEnvelopeOverride = openStackId !== undefined && envelopeOverrides?.has(openStackId) === true;
    const childrenQuery = useQuery({
        queryKey: ["stream.stackChildren", openStackId],
        queryFn: async () => {
            // `enabled` below guarantees `openStackId` is defined when this
            // runs — assert for the type-checker.
            const id = openStackId;
            if (id === undefined) {
                throw new Error("stackChildren queryFn called without an id");
            }
            const data = await getCollectionDetails(id, { limit: STACK_DETAIL_PAGE_SIZE });
            // Mirror the fetched envelopes into `blockCollection` so each
            // unfurled card's `blockStore.useById(blockId)` resolves on its
            // first render. Without this the cards mount, look up their
            // envelopes against an empty live query (the unfurl typically
            // contains older blocks that never made it into the homepage's
            // recent-feed seed), and render the pending-skeleton spinner
            // indefinitely — the children query resolves but the per-card
            // cache misses are never filled because `useById` has no
            // network fallback (see the comment at the top of
            // `block-store.ts#useById` for the rationale).
            blockStore.cacheEnvelopes(data.blocks);
            return data;
        },
        enabled: openStackId !== undefined && hasEnvelopeOverride === false,
        staleTime: 30_000,
    });
    const rawEnvelopes = hasEnvelopeOverride
        ? envelopeOverrides.get(openStackId)
        : childrenQuery.data?.blocks;
    // -- Pin-aware reorder --------------------------------------------------
    //
    // The unfurled stack honors its own collection-scoped pins (the same
    // `(collection:<stackId>, "default")` slot the per-card "Pin to <stack>"
    // toolbar writes to). Pinned blocks float to the front in pin order;
    // every other block keeps the server's `surfaceAt`-descending order.
    //
    // The subscription always runs (hooks must be unconditional), but
    // resolves to a no-op `{type: "account"}` slot when no stack is open
    // so we don't re-subscribe on every `openStackId` change to an empty
    // list.
    const pinScope = useMemo(() => openStackId !== undefined
        ? { type: "collection", id: openStackId }
        : NOOP_PIN_SCOPE, [openStackId]);
    const pinOrder = usePinList(pinScope, "default");
    const envelopes = useMemo(() => {
        if (rawEnvelopes === undefined)
            return undefined;
        if (openStackId === undefined)
            return rawEnvelopes;
        if (pinOrder.length === 0)
            return rawEnvelopes;
        const byBlockId = new Map();
        for (const env of rawEnvelopes)
            byBlockId.set(env.block.id, env);
        const pinned = [];
        const pinnedKeys = new Set();
        for (const p of pinOrder) {
            if (p.type !== "block")
                continue;
            const env = byBlockId.get(p.id);
            if (env === undefined)
                continue;
            pinned.push(env);
            pinnedKeys.add(p.id);
        }
        if (pinned.length === 0)
            return rawEnvelopes;
        const rest = rawEnvelopes.filter((e) => pinnedKeys.has(e.block.id) === false);
        return [...pinned, ...rest];
    }, [rawEnvelopes, pinOrder, openStackId]);
    // Warm annotation rows for unfurled children regardless of where
    // the envelope list came from (prefetched query cache, on-demand
    // fetch, or synthetic override). This keeps stack blocks'
    // sticker/stamp/drawing overlays from mounting "empty" on first
    // open when only block envelopes were pre-cached.
    const unfurlBlockIds = useMemo(() => envelopes?.map((envelope) => envelope.block.id) ?? [], [envelopes]);
    useAnnotationsForBlocks(unfurlBlockIds);
    useEffect(() => {
        if (storedEnvelopes === envelopes)
            return;
        app.setStackUnfurl({ envelopes });
    }, [app, storedEnvelopes, envelopes]);
    // -- Phase transitions --------------------------------------------------
    // loading -> opening once envelopes resolve. Gated on `envelopes
    // !== undefined` rather than `isFetching === false` so a background
    // refetch while open doesn't bounce the user out.
    useEffect(() => {
        if (phase !== "loading")
            return;
        if (envelopes === undefined)
            return;
        setPhase("opening");
    }, [phase, envelopes, setPhase]);
    // -- toggleStack: snapshot synchronously, then transition state ---------
    const toggleStack = useCallback((stack) => {
        const currentPhase = phaseRef.current;
        // Click the same stack while it's open / opening / loading -> close.
        if (openStackId === stack.id && currentPhase !== "closed") {
            if (currentPhase === "closing")
                return;
            stackTileBeforeRectRef.current = lookupStackTile(stack.id)?.getBoundingClientRect();
            beforeRectsRef.current = snapshotGridRects();
            cancelPending();
            hasRunOpenFlipRef.current = false;
            setPhase("closing");
            return;
        }
        // Click a different stack while one is open -> queue + close current.
        if (openStackId !== undefined && currentPhase !== "closed") {
            pendingOpenStackRef.current = stack;
            // Snapshot the *current* layout so the close FLIP anchors on
            // the open stack we're about to dismiss, not the new one.
            const currentTileId = openStackId;
            stackTileBeforeRectRef.current = lookupStackTile(currentTileId)?.getBoundingClientRect();
            beforeRectsRef.current = snapshotGridRects();
            cancelPending();
            hasRunOpenFlipRef.current = false;
            setPhase("closing");
            return;
        }
        // Fresh open.
        cancelPending();
        hasRunOpenFlipRef.current = false;
        stackTileBeforeRectRef.current = lookupStackTile(stack.id)?.getBoundingClientRect();
        beforeRectsRef.current = snapshotGridRects();
        setOpenStackId(stack.id);
        setPhase("loading");
    }, [openStackId, cancelPending, setOpenStackId, setPhase]);
    // -- ESC handler --------------------------------------------------------
    useEffect(() => {
        if (phase === "closed" || phase === "closing")
            return;
        function onKeyDown(e) {
            if (e.key !== "Escape")
                return;
            if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey)
                return;
            const id = openStackId;
            if (id === undefined)
                return;
            // Multi-select wins Esc. When both an open stack AND an active
            // multi-select selection are visible, the user's first Esc should
            // exit the selection chrome — closing the stack would yank the
            // surface out from under the in-progress action. The multi-select
            // handler in `multiselect-action-bar.tsx` is attached to `window`,
            // which fires AFTER this `document` handler in the bubble phase,
            // so simply bailing here (no `preventDefault`, no
            // `stopPropagation`) lets that handler run unimpeded. Pressing
            // Esc again — with multi-select now off — closes the stack.
            if (app.state.ui.isMultiselectMode)
                return;
            e.preventDefault();
            // Mirror toggleStack's snapshot + close path.
            stackTileBeforeRectRef.current = lookupStackTile(id)?.getBoundingClientRect();
            beforeRectsRef.current = snapshotGridRects();
            cancelPending();
            hasRunOpenFlipRef.current = false;
            setPhase("closing");
        }
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [phase, openStackId, cancelPending, app]);
    // -- FLIP open ----------------------------------------------------------
    useLayoutEffect(() => {
        if (phase !== "opening")
            return;
        if (envelopes === undefined)
            return;
        if (hasRunOpenFlipRef.current)
            return;
        // The StreamGroup's own layout effect runs before this one
        // (children-first effect order), but the unfurl items mount in
        // response to `showUnfurledItems` which flips when `phase` becomes
        // "opening". Wait until every envelope's element has been
        // registered before snapping FLIP — otherwise the new items would
        // get skipped and pop into place uncoordinated.
        let allMounted = true;
        for (const env of envelopes) {
            if (!unfurlItemRefsRef.current.has(env.block.id)) {
                allMounted = false;
                break;
            }
        }
        if (!allMounted)
            return;
        hasRunOpenFlipRef.current = true;
        const before = beforeRectsRef.current;
        const stackBefore = stackTileBeforeRectRef.current;
        const allChildren = walkAllGridChildren();
        // Build an ordered map of unfurl items so we can assign a stagger
        // index. `unfurlItemRefsRef` is keyed by blockId; order it by the
        // envelope array so the stagger follows reading order (row-by-row,
        // left-to-right) rather than Map insertion order (which is
        // coincidentally the same here but should be explicit).
        const unfurlItemsOrdered = [];
        for (const env of envelopes) {
            const el = unfurlItemRefsRef.current.get(env.block.id);
            if (el)
                unfurlItemsOrdered.push(el);
        }
        const newItemEls = new Set(unfurlItemsOrdered);
        const newItemIndex = new Map(unfurlItemsOrdered.map((el, i) => [el, i]));
        // Maximum stagger offset (caps the cascade at MAX_STAGGER_ITEMS).
        const maxStaggerDelay = Math.min(unfurlItemsOrdered.length, MAX_STAGGER_ITEMS) * UNFURL_STAGGER_MS;
        const touched = [];
        for (const el of allChildren) {
            const after = el.getBoundingClientRect();
            let dx = 0;
            let dy = 0;
            let isNewItem = false;
            const beforeRect = before.get(el);
            if (beforeRect) {
                dx = beforeRect.left - after.left;
                dy = beforeRect.top - after.top;
            }
            else if (newItemEls.has(el) && stackBefore) {
                // New unfurl item — animate from the stack tile's BEFORE rect.
                dx = stackBefore.left - after.left;
                dy = stackBefore.top - after.top;
                isNewItem = true;
            }
            else {
                // Element wasn't in the snapshot and isn't a new unfurl item.
                // Could be an unrelated DOM mutation (e.g. a new pinned item
                // sliding in) — leave it alone.
                continue;
            }
            // New items always need the snap so they can scale-up during the
            // FLIP, even on the rare 0-delta case. Sibling cards skip a no-op
            // animation.
            if (!isNewItem && dx === 0 && dy === 0)
                continue;
            el.style.transition = "none";
            el.style.transform = isNewItem
                ? `translate3d(${dx}px, ${dy}px, 0) scale(${CLOSE_SCALE})`
                : `translate3d(${dx}px, ${dy}px, 0)`;
            touched.push(el);
        }
        // Force reflow so the snap commits before the transition starts.
        void document.body.offsetHeight;
        // Safari can skip transitions on newly mounted elements if the
        // inverse transform and the play transform both land before the
        // first paint. The first rAF gives the snapped state one frame to
        // paint; the second starts the transition.
        pendingRafRef.current = requestAnimationFrame(() => {
            if (phaseRef.current !== "opening")
                return;
            pendingRafRef.current = requestAnimationFrame(() => {
                pendingRafRef.current = undefined;
                if (phaseRef.current !== "opening")
                    return;
                for (const el of touched) {
                    const idx = newItemIndex.get(el);
                    // Unfurl items get a cascade stagger delay embedded directly
                    // in the transition shorthand. Setting `transitionDelay`
                    // separately won't work — the `transition` shorthand resets
                    // all sub-properties (including delay) to their defaults,
                    // clobbering any separately-set `transitionDelay`.
                    const staggerDelay = idx !== undefined ? Math.min(idx, MAX_STAGGER_ITEMS - 1) * UNFURL_STAGGER_MS : 0;
                    el.style.transition = `transform ${UNFURL_ANIM_MS}ms ${UNFURL_EASING} ${staggerDelay}ms`;
                    el.style.transform =
                        idx !== undefined ? IDENTITY_TRANSLATE_SCALE : IDENTITY_TRANSLATE;
                }
            });
        });
        // The opening → open phase transition must wait for the LAST
        // unfurl item to finish, which is UNFURL_ANIM_MS after its own
        // stagger delay kicks in.
        pendingTimerRef.current = window.setTimeout(() => {
            pendingTimerRef.current = undefined;
            if (phaseRef.current !== "opening")
                return;
            for (const el of touched) {
                el.style.transition = "";
                el.style.transform = "";
            }
            setPhase("open");
        }, UNFURL_ANIM_MS + maxStaggerDelay + UNFURL_SAFETY_MS);
        return () => {
            cancelPending();
            for (const el of touched) {
                el.style.transition = "";
                el.style.transform = "";
            }
        };
    }, [phase, envelopes, cancelPending]);
    // -- FLIP close ---------------------------------------------------------
    useLayoutEffect(() => {
        if (phase !== "closing")
            return;
        const before = beforeRectsRef.current;
        const stackBefore = stackTileBeforeRectRef.current;
        const closingStackId = openStackId;
        const allChildren = walkAllGridChildren();
        // Build envelope-ordered list (mirror of open) so the close stagger
        // is the exact inverse of the open stagger: card 0 (front-most)
        // arrived first on open, so it should leave LAST on close; card N
        // (back-most) arrived last on open, so it leaves FIRST on close.
        const unfurlItemsOrdered = [];
        if (envelopes !== undefined) {
            for (const env of envelopes) {
                const el = unfurlItemRefsRef.current.get(env.block.id);
                if (el)
                    unfurlItemsOrdered.push(el);
            }
        }
        // Fallback: any items not yet in the envelopes list (shouldn't
        // happen during a normal close, but defensive against torn-down
        // queries) come last in insertion order.
        for (const el of unfurlItemRefsRef.current.values()) {
            if (!unfurlItemsOrdered.includes(el))
                unfurlItemsOrdered.push(el);
        }
        const unfurlEls = unfurlItemsOrdered;
        const unfurlElSet = new Set(unfurlEls);
        const newItemIndex = new Map(unfurlEls.map((el, i) => [el, i]));
        // Maximum stagger budget for the close-phase timer extension.
        // Mirrors the open-phase math exactly.
        const maxStaggerDelay = Math.min(unfurlEls.length, MAX_STAGGER_ITEMS) * UNFURL_STAGGER_MS;
        const totalStaggered = Math.min(unfurlEls.length, MAX_STAGGER_ITEMS);
        // Step A: take unfurl items out of flow at their BEFORE rects.
        // This frees up their grid cells so the surrounding rows can
        // reflow upward, and gives us absolutely-positioned closers we
        // can animate toward the stack tile.
        const unfurlEntries = [];
        for (const el of unfurlEls) {
            const parent = el.parentElement;
            if (!parent)
                continue;
            const beforeRect = before.get(el);
            if (!beforeRect)
                continue;
            const parentRect = parent.getBoundingClientRect();
            const saved = saveItemStyle(el);
            unfurlEntries.push({ el, saved, parent });
            el.style.position = "absolute";
            el.style.top = `${beforeRect.top - parentRect.top}px`;
            el.style.left = `${beforeRect.left - parentRect.left}px`;
            el.style.width = `${beforeRect.width}px`;
            el.style.height = `${beforeRect.height}px`;
            el.style.margin = "0";
            el.style.transition = "none";
            el.style.transform = "";
            el.style.opacity = "1";
        }
        // Step B: measure new sibling positions (post-reflow); apply
        // inverse transforms to put them visually at BEFORE.
        const siblingTouched = [];
        for (const el of allChildren) {
            if (unfurlElSet.has(el))
                continue;
            const beforeRect = before.get(el);
            if (!beforeRect)
                continue;
            const after = el.getBoundingClientRect();
            const dx = beforeRect.left - after.left;
            const dy = beforeRect.top - after.top;
            if (dx === 0 && dy === 0)
                continue;
            el.style.transition = "none";
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            siblingTouched.push(el);
        }
        // Force reflow.
        void document.body.offsetHeight;
        pendingRafRef.current = requestAnimationFrame(() => {
            pendingRafRef.current = undefined;
            if (phaseRef.current !== "closing")
                return;
            // Siblings: transition back to AFTER (transform="").
            for (const el of siblingTouched) {
                el.style.transition = `transform ${UNFURL_ANIM_MS}ms ${UNFURL_EASING}`;
                el.style.transform = "";
            }
            // Unfurl items: transition toward the (post-reflow) stack tile
            // rect. The tile's NEW position differs from its BEFORE rect
            // when there were items in the same row — but during open the
            // stack tile's row didn't move, so in practice these usually
            // converge. Fall back to `stackBefore` if the tile is no longer
            // in the DOM (rare — collection refetch dropped it).
            const stackAfter = closingStackId !== undefined
                ? lookupStackTile(closingStackId)?.getBoundingClientRect()
                : undefined;
            for (const { el, parent } of unfurlEntries) {
                const target = stackAfter ?? stackBefore;
                if (!target)
                    continue;
                const parentRect = parent.getBoundingClientRect();
                const targetTop = target.top - parentRect.top;
                const targetLeft = target.left - parentRect.left;
                const currentTop = parseFloat(el.style.top) || 0;
                const currentLeft = parseFloat(el.style.left) || 0;
                const dx = targetLeft - currentLeft;
                const dy = targetTop - currentTop;
                // Reversed stagger: idx 0 (front-most) leaves last; idx N
                // (back-most) leaves first. Mirrors open's stagger so the close
                // looks like the open played in reverse.
                const idx = newItemIndex.get(el);
                const reversedIdx = idx !== undefined
                    ? totalStaggered - 1 - Math.min(idx, MAX_STAGGER_ITEMS - 1)
                    : 0;
                const staggerDelay = Math.max(0, reversedIdx) * UNFURL_STAGGER_MS;
                el.style.transition = `transform ${UNFURL_ANIM_MS}ms ${UNFURL_EASING} ${staggerDelay}ms`;
                el.style.transform = `translate(${dx}px, ${dy}px) scale(${CLOSE_SCALE})`;
            }
        });
        pendingTimerRef.current = window.setTimeout(() => {
            pendingTimerRef.current = undefined;
            // Restore sibling styles so they continue to obey the grid on
            // the next render.
            for (const el of siblingTouched) {
                el.style.transition = "";
                el.style.transform = "";
            }
            // Deliberately NOT restoring inline styles on the unfurl items:
            // they're about to unmount as `showUnfurledItems` flips to false
            // on the upcoming render, and restoring would briefly snap them
            // back to grid position at opacity 1 — a visible one-frame
            // flicker between the restore and the unmount commit. Letting
            // them unmount with the close-FLIP styles intact (`opacity: 0;
            // transform: translate(...)`) means the items go straight from
            // invisible → unmounted. The cleanup return-value still
            // restores them for the mid-flight teardown case (phase
            // preempted to a non-closed state, where items don't unmount).
            setOpenStackId(undefined);
            setPhase("closed");
            // Sync the phase ref synchronously: the queued-open microtask
            // below guards on `phaseRef.current === "closed"`, but the
            // useEffect that mirrors `phase -> phaseRef.current` doesn't run
            // until after React commits. Microtasks fire BEFORE the commit
            // effect, so without this line the guard would still see
            // `"closing"` and drop a legitimate queued open with a
            // false-positive "raced ahead" warning.
            phaseRef.current = "closed";
            // Pick up any queued open. Defer to a microtask so React commits
            // the close (state -> closed, openStackId -> undefined) before
            // the next open snapshots rects.
            const queued = pendingOpenStackRef.current;
            if (queued !== undefined) {
                pendingOpenStackRef.current = undefined;
                queueMicrotask(() => {
                    if (phaseRef.current !== "closed") {
                        // A new toggleStack already raced ahead — drop the queued
                        // open rather than racing it.
                        logger.warn("queued open dropped: phase changed mid-flush", {
                            stackId: queued.id,
                            phase: phaseRef.current,
                        });
                        return;
                    }
                    // Snapshot a fresh BEFORE state.
                    stackTileBeforeRectRef.current = lookupStackTile(queued.id)?.getBoundingClientRect();
                    beforeRectsRef.current = snapshotGridRects();
                    hasRunOpenFlipRef.current = false;
                    setOpenStackId(queued.id);
                    setPhase("loading");
                });
            }
            // Timer delay = anim + max stagger + safety, so the LAST card
            // (front-most, longest stagger delay in the reversed sequence)
            // finishes its transition before this callback runs.
        }, UNFURL_ANIM_MS + maxStaggerDelay + UNFURL_SAFETY_MS);
        return () => {
            cancelPending();
            for (const el of siblingTouched) {
                el.style.transition = "";
                el.style.transform = "";
            }
            for (const { el, saved } of unfurlEntries) {
                restoreItemStyle(el, saved);
            }
        };
    }, [phase, openStackId, cancelPending]);
    // -- Derived ------------------------------------------------------------
    const registerItemRef = useCallback((blockId, el) => {
        if (el) {
            // BlockViewContainer's forwarded ref targets an inner element
            // (`.block-view` on the non-pending render path, the pending
            // wrapper on the loading path) — NOT the outer
            // `.block-view-host` which is the actual `.stream-group__cards`
            // direct child. The FLIP math depends on registering the grid
            // child (so `walkAllGridChildren()` and `unfurlItemRefsRef`
            // hold the same DOM nodes; otherwise `before.get(el)` misses
            // and step A of the close FLIP silently bails on every item,
            // leaving them un-animated).
            //
            // Walk up the parent chain until the element directly under a
            // `.stream-group__cards` is found, and register THAT.
            let host = el;
            let foundGridChild = false;
            while (host !== null) {
                const parentEl = host.parentElement;
                if (parentEl === null)
                    break;
                if (parentEl.classList.contains("stream-group__cards")) {
                    foundGridChild = true;
                    break;
                }
                host = parentEl;
            }
            if (foundGridChild && host !== null) {
                unfurlItemRefsRef.current.set(blockId, host);
                host.dataset.unfurlItem = "true";
            }
        }
        else {
            unfurlItemRefsRef.current.delete(blockId);
        }
    }, []);
    useEffect(() => {
        app.setStackUnfurlController(toggleStack, registerItemRef);
        return () => {
            app.clearStackUnfurlController(toggleStack);
        };
    }, [app, toggleStack, registerItemRef]);
    return {
        openStackId,
        envelopes,
        isLoading: childrenQuery.isLoading,
        phase,
        showUnfurledItems,
        toggleStack,
        registerItemRef,
    };
}
